import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';

export type PilRetning = 'opp' | 'ned' | 'venstre' | 'hoyre';

export type CoachmarkValg = {
  targetRect: { x: number; y: number; w: number; h: number };
  tekst: string;
  pilRetning: PilRetning;
  preferertSide?: 'over' | 'under';
  onHoppOver?: () => void;
};

const BOBLE_BREDDE = 800;
const BOBLE_PADDING = 32;
const PIL_LENGDE = 36;
const PIL_BREDDE = 28;
const RING_BREDDE = 6;

export class CoachmarkManager {
  private layer?: Phaser.GameObjects.Container;
  private ringTween?: Phaser.Tweens.Tween;

  constructor(private scene: Phaser.Scene) {}

  vis(valg: CoachmarkValg): void {
    this.skjul();
    this.layer = this.scene.add.container(0, 0).setDepth(400);

    this.tegnDempetBackdrop(valg.targetRect);
    this.tegnRing(valg.targetRect);
    this.tegnBoble(valg);
    this.tegnHoppOver(valg.onHoppOver);
  }

  skjul(): void {
    this.ringTween?.stop();
    this.ringTween = undefined;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get erSynlig(): boolean {
    return !!this.layer;
  }

  private tegnDempetBackdrop(rect: CoachmarkValg['targetRect']): void {
    if (!this.layer) return;
    const padding = 12;
    const tx = rect.x - padding;
    const ty = rect.y - padding;
    const tw = rect.w + padding * 2;
    const th = rect.h + padding * 2;
    const alpha = 0.55;

    const topp = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, Math.max(0, ty), 0x000000, alpha)
      .setOrigin(0, 0)
      .setInteractive();
    const bunn = this.scene.add
      .rectangle(0, ty + th, GAME_WIDTH, Math.max(0, GAME_HEIGHT - (ty + th)), 0x000000, alpha)
      .setOrigin(0, 0)
      .setInteractive();
    const venstre = this.scene.add
      .rectangle(0, ty, Math.max(0, tx), th, 0x000000, alpha)
      .setOrigin(0, 0)
      .setInteractive();
    const hoyre = this.scene.add
      .rectangle(tx + tw, ty, Math.max(0, GAME_WIDTH - (tx + tw)), th, 0x000000, alpha)
      .setOrigin(0, 0)
      .setInteractive();

    this.layer.add([topp, bunn, venstre, hoyre]);
  }

  private tegnRing(rect: CoachmarkValg['targetRect']): void {
    if (!this.layer) return;
    const padding = 8;
    const radius = Math.max(rect.w, rect.h) / 2 + padding;
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;

    const ring = this.scene.add
      .circle(cx, cy, radius, 0xffffff, 0)
      .setStrokeStyle(RING_BREDDE, COLORS.wine, 1);
    this.layer.add(ring);

    this.ringTween = this.scene.tweens.add({
      targets: ring,
      scale: { from: 1, to: 1.12 },
      alpha: { from: 1, to: 0.55 },
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private tegnBoble(valg: CoachmarkValg): void {
    if (!this.layer) return;
    const rect = valg.targetRect;

    const tempTekst = this.scene.add
      .text(0, 0, valg.tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(28),
        color: '#1a1a1a',
        fontStyle: '600',
        wordWrap: { width: BOBLE_BREDDE - BOBLE_PADDING * 2 },
        lineSpacing: 6,
      })
      .setOrigin(0, 0);
    const tekstH = tempTekst.height;
    tempTekst.destroy();

    const knappH = 72;
    const knappGap = 24;
    const bobleW = BOBLE_BREDDE;
    const bobleH = tekstH + BOBLE_PADDING * 2 + knappGap + knappH;

    const senterX = rect.x + rect.w / 2;
    let bobleX = senterX - bobleW / 2;
    bobleX = Math.max(40, Math.min(GAME_WIDTH - bobleW - 40, bobleX));

    const margin = 60;
    const overY = rect.y - bobleH - margin;
    const underY = rect.y + rect.h + margin;
    const passerOver = overY > 80;
    const passerUnder = underY + bobleH < GAME_HEIGHT - 80;

    const foretrukket = valg.preferertSide
      ?? (valg.pilRetning === 'opp' ? 'under' : valg.pilRetning === 'ned' ? 'over' : 'under');
    let bobleY: number;
    let pilOpp: boolean;
    if (foretrukket === 'over' && passerOver) {
      bobleY = overY;
      pilOpp = false;
    } else if (foretrukket === 'under' && passerUnder) {
      bobleY = underY;
      pilOpp = true;
    } else if (passerUnder) {
      bobleY = underY;
      pilOpp = true;
    } else if (passerOver) {
      bobleY = overY;
      pilOpp = false;
    } else {
      bobleY = 80;
      pilOpp = true;
    }

    const skygge = this.scene.add
      .rectangle(bobleX + 6, bobleY + 8, bobleW, bobleH, 0x000000, 0.3)
      .setOrigin(0, 0);
    const bg = this.scene.add
      .rectangle(bobleX, bobleY, bobleW, bobleH, COLORS.yellowWarm, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.ink, 0.8);
    this.layer.add([skygge, bg]);

    const tekst = this.scene.add
      .text(bobleX + BOBLE_PADDING, bobleY + BOBLE_PADDING, valg.tekst, {
        fontFamily: FONTS.body,
        fontSize: fontStr(28),
        color: '#1a1a1a',
        fontStyle: '600',
        wordWrap: { width: BOBLE_BREDDE - BOBLE_PADDING * 2 },
        lineSpacing: 6,
      })
      .setOrigin(0, 0);
    this.layer.add(tekst);

    const knappY = bobleY + bobleH - knappH - 16;
    const knappW = 260;
    const knappX = bobleX + bobleW - knappW - 24;
    const fortsettBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const fortsettTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Skjønner', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([fortsettBg, fortsettTxt]);
    fortsettBg.setInteractive({ useHandCursor: true });
    fortsettBg.on('pointerover', () => fortsettBg.setFillStyle(0x6f1f2c));
    fortsettBg.on('pointerout', () => fortsettBg.setFillStyle(COLORS.wine));
    fortsettBg.on('pointerup', () => this.skjul());

    this.tegnPil(senterX, bobleX, bobleY, bobleW, bobleH, pilOpp, rect);
  }

  private tegnPil(
    senterX: number,
    bobleX: number,
    bobleY: number,
    bobleW: number,
    bobleH: number,
    pilOpp: boolean,
    rect: CoachmarkValg['targetRect'],
  ): void {
    if (!this.layer) return;
    const pilSenterX = Math.max(bobleX + 32, Math.min(bobleX + bobleW - 32, senterX));
    const pil = this.scene.add.graphics();
    pil.fillStyle(COLORS.yellowWarm, 1);
    pil.lineStyle(3, COLORS.ink, 0.8);

    if (pilOpp) {
      const baseY = bobleY;
      const tippY = baseY - PIL_LENGDE;
      pil.beginPath();
      pil.moveTo(pilSenterX - PIL_BREDDE / 2, baseY);
      pil.lineTo(pilSenterX + PIL_BREDDE / 2, baseY);
      pil.lineTo(pilSenterX, tippY);
      pil.closePath();
      pil.fillPath();
      pil.strokePath();
    } else {
      const baseY = bobleY + bobleH;
      const tippY = baseY + PIL_LENGDE;
      pil.beginPath();
      pil.moveTo(pilSenterX - PIL_BREDDE / 2, baseY);
      pil.lineTo(pilSenterX + PIL_BREDDE / 2, baseY);
      pil.lineTo(pilSenterX, tippY);
      pil.closePath();
      pil.fillPath();
      pil.strokePath();
    }
    this.layer.add(pil);
    void rect;
  }

  private tegnHoppOver(onHoppOver?: () => void): void {
    if (!this.layer || !onHoppOver) return;
    const x = 40;
    const y = GAME_HEIGHT - 56;
    const txt = this.scene.add
      .text(x, y, 'Hopp over alt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#cccccc',
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    txt.on('pointerover', () => txt.setColor('#ffffff'));
    txt.on('pointerout', () => txt.setColor('#cccccc'));
    txt.on('pointerup', () => {
      this.skjul();
      onHoppOver();
    });
    this.layer.add(txt);
  }
}
